import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { SITE_URL } from '@/config/site.mjs';
import { formatDuration } from '@/shared/utils/format/formatCompactDuration.mjs';
import factions from '@/shared/enums/factions.mjs';
import map from '@/shared/enums/map.mjs';
import { EVENT_TYPE, EVENT_STATUS } from '@/shared/enums/events.mjs';
import EventCardLayout, { STATUS_STYLES } from '@/features/timeline/EventCardLayout';
import { formatCompactDuration } from '@/shared/utils/format/formatCompactDuration.mjs';
import { getEventActionLabel } from '@/shared/utils/game/getEventActionLabel.mjs';
import { getEventRegionLabel } from '@/shared/utils/game/getEventRegionLabel.mjs';

/**
 * Unified event log card. Replaces both the live dashboard `Event.jsx` and
 * the historical `ArchiveEvent.jsx`.
 *
 * `timeFormat` controls the secondary time line:
 * - `'live'`    → ticking relative text ("Started 2 hours ago" / "Ended 3 days ago")
 * - `'absolute'` → static absolute date/time ("Mar 3, 2026 · 14:23")
 *
 * Everything else (region, action label, points progress, duration pill,
 * faction icon, status styling, JSON-LD) is identical across both modes.
 */
/**
 * @param {object} props - Component props.
 * @param {import('@/features/dashboard/DashboardClient').LiveEvent} props.event - The event to render.
 * @param {'live' | 'absolute'} [props.timeFormat] - Secondary time line mode (ticking relative vs static absolute).
 * @param {boolean} [props.isSelected] - Whether the card is highlighted as selected.
 * @param {import('react').MouseEventHandler} [props.onMouseEnter] - Hover-enter handler.
 * @param {import('react').MouseEventHandler} [props.onMouseLeave] - Hover-leave handler.
 */
export default function EventLogCard(t0) {
  const $ = _c(42);
  const {
    event,
    timeFormat,
    isSelected: t1,
    onMouseEnter,
    onMouseLeave
  } = t0;
  const isSelected = t1 === undefined ? false : t1;
  const isCompleted = event.status === EVENT_STATUS.SUCCESS || event.status === EVENT_STATUS.FAIL;
  let t2;
  if ($[0] !== event) {
    t2 = getEventActionLabel(event);
    $[0] = event;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const actionLabel = t2;
  let t3;
  if ($[2] !== event) {
    t3 = getEventRegionLabel(event);
    $[2] = event;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const regionLabel = t3;
  const t4 = event.points / event.points_max * 100;
  let t5;
  if ($[4] !== t4) {
    t5 = t4.toFixed(2);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const percent = t5;
  const faction = factions[event.enemy];
  const s = STATUS_STYLES[event.status] || STATUS_STYLES.active;
  let t6;
  if ($[6] !== event) {
    t6 = JSON.stringify(schema(event, event.type));
    $[6] = event;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const jsonLdString = t6;
  const t7 = isSelected ? "border-l-[4px] border-l-primary !bg-primary-tint" : "";
  let t8;
  if ($[8] !== actionLabel || $[9] !== regionLabel) {
    t8 = <span className="font-body text-small font-bold text-text uppercase">{actionLabel} {regionLabel}</span>;
    $[8] = actionLabel;
    $[9] = regionLabel;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== event || $[12] !== s || $[13] !== timeFormat) {
    t9 = <DurationPill event={event} styles={s} timeFormat={timeFormat} />;
    $[11] = event;
    $[12] = s;
    $[13] = timeFormat;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== faction) {
    t10 = faction && <Image src={faction.icon} alt={faction.name} width={20} height={20} className="size-5" />;
    $[15] = faction;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8 || $[19] !== t9) {
    t11 = <div className="flex items-center justify-between gap-2">{t8}{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== event || $[22] !== isCompleted || $[23] !== timeFormat) {
    t12 = <TimeLine event={event} timeFormat={timeFormat} isCompleted={isCompleted} />;
    $[21] = event;
    $[22] = isCompleted;
    $[23] = timeFormat;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== event.points || $[26] !== event.points_max || $[27] !== percent) {
    t13 = <div className="font-mono text-small text-text-muted">{event.points} / {event.points_max} ({percent}%)</div>;
    $[25] = event.points;
    $[26] = event.points_max;
    $[27] = percent;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t12 || $[31] !== t13) {
    t14 = <div className="flex flex-col gap-1 px-2.5 py-1.5">{t11}{t12}{t13}</div>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== jsonLdString) {
    t15 = <JsonLdSchema jsonLd={jsonLdString} />;
    $[33] = jsonLdString;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== event.status || $[36] !== onMouseEnter || $[37] !== onMouseLeave || $[38] !== t14 || $[39] !== t15 || $[40] !== t7) {
    t16 = <EventCardLayout status={event.status} className={t7} onClick={undefined} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>{t14}{t15}</EventCardLayout>;
    $[35] = event.status;
    $[36] = onMouseEnter;
    $[37] = onMouseLeave;
    $[38] = t14;
    $[39] = t15;
    $[40] = t7;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}

/**
 * Compact duration pill. In `live` mode the duration ticks for active
 * events; in `absolute` mode it shows the final duration (end − start)
 * even for active events, since there's no clock to tick against.
 */
function DurationPill(t0) {
  const $ = _c(8);
  const {
    event,
    styles,
    timeFormat
  } = t0;
  const [now, setNow] = useState(_temp);
  const isCompleted = event.status === EVENT_STATUS.SUCCESS || event.status === EVENT_STATUS.FAIL;
  const shouldTick = timeFormat === "live" && !isCompleted;
  let t1;
  let t2;
  if ($[0] !== shouldTick) {
    t1 = () => {
      if (!shouldTick) {
        return;
      }
      const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
      return () => clearInterval(id);
    };
    t2 = [shouldTick];
    $[0] = shouldTick;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const duration = isCompleted || timeFormat === "absolute" ? event.end_time - event.start_time : now - event.start_time;
  const t3 = `px-1.5 py-px font-mono text-[10px] ${styles.pill}`;
  let t4;
  if ($[3] !== duration) {
    t4 = formatCompactDuration(duration);
    $[3] = duration;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <span className={t3} suppressHydrationWarning={true}>{t4}</span>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/**
 * Secondary time line — either live "Started/Ended X ago" (ticks every
 * second) or a static absolute date/time like "Mar 3, 2026 · 14:23".
 */
function _temp() {
  return Math.floor(Date.now() / 1000);
}
function TimeLine(t0) {
  const $ = _c(8);
  const {
    event,
    timeFormat,
    isCompleted
  } = t0;
  if (timeFormat === "absolute") {
    const ts = isCompleted ? event.end_time : event.start_time;
    let t1;
    if ($[0] !== ts) {
      t1 = new Date(ts * 1000).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC"
      });
      $[0] = ts;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const formatted = t1;
    const prefix = isCompleted ? "Ended" : "Started";
    let t2;
    if ($[2] !== formatted || $[3] !== prefix) {
      t2 = <span className="text-small text-text-muted">{prefix} {formatted}</span>;
      $[2] = formatted;
      $[3] = prefix;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] !== event || $[6] !== isCompleted) {
    t1 = <LiveTimeLine event={event} isCompleted={isCompleted} />;
    $[5] = event;
    $[6] = isCompleted;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function LiveTimeLine(t0) {
  const $ = _c(7);
  const {
    event,
    isCompleted
  } = t0;
  const [now, setNow] = useState(_temp2);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
      return () => clearInterval(id);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const elapsed = isCompleted ? now - event.end_time : now - event.start_time;
  let t3;
  if ($[2] !== elapsed || $[3] !== isCompleted) {
    t3 = isCompleted ? `Ended ${formatDuration(elapsed)} ago` : `Started ${formatDuration(elapsed)} ago`;
    $[2] = elapsed;
    $[3] = isCompleted;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const text = t3;
  let t4;
  if ($[5] !== text) {
    t4 = <span className="text-small text-text-muted" suppressHydrationWarning={true}>{text}</span>;
    $[5] = text;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// Structured data <script> rendered via a stub that gets its innerHTML
// replaced on insertion. Trusted content (JSON.stringify of DB fields);
// no user input, no XSS surface. Extracted into its own component purely
// to keep the main render tree free of dangerouslySetInnerHTML noise.
function _temp2() {
  return Math.floor(Date.now() / 1000);
}
function JsonLdSchema(t0) {
  const $ = _c(2);
  const {
    jsonLd
  } = t0;
  let t1;
  if ($[0] !== jsonLd) {
    t1 = <script type="application/ld+json" suppressHydrationWarning={true} ref={el => {
      if (el && el.textContent !== jsonLd) {
        el.textContent = jsonLd;
      }
    }} />;
    $[0] = jsonLd;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function schema(event, type) {
  if (type === EVENT_TYPE.ATTACK) {
    const {
      capital,
      region
    } = map[event.enemy][11];
    const faction = factions[event.enemy].name;
    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: `Capturing ${capital}`,
      description: `The Helldivers have launched an assault on ${capital} in the ${region}. Join the fight to liberate this sector from ${faction} control!`,
      startDate: new Date(event.start_time * 1000),
      endDate: new Date(event.end_time * 1000),
      image: [`${SITE_URL}/icons/attack.webp`],
      location: {
        '@type': 'VirtualLocation',
        url: SITE_URL,
        name: `${capital}, ${region}`
      },
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      performer: {
        '@type': 'PerformingGroup',
        name: 'Helldivers'
      },
      organizer: {
        '@type': 'Organization',
        name: factions[3].name,
        url: factions[3].url
      },
      offers: {
        '@type': 'Offer',
        url: SITE_URL,
        price: 0,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        validFrom: new Date(event.start_time * 1000)
      }
    };
  }
  if (type === EVENT_TYPE.DEFEND) {
    const enemy = event.region === 0 ? 3 : event.enemy;
    const {
      capital,
      region
    } = map[enemy][event.region];
    const faction = factions[enemy].name;
    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: `Defend ${capital}`,
      description: `Cowardly ${faction} has attacked the innocent city of ${capital} in the ${region}. Get together and defend against this xeno threat!`,
      startDate: new Date(event.start_time * 1000),
      endDate: new Date(event.end_time * 1000),
      image: [`${SITE_URL}/icons/defend.webp`],
      location: {
        '@type': 'VirtualLocation',
        url: SITE_URL,
        name: `${capital}, ${region}`
      },
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      performer: {
        '@type': 'PerformingGroup',
        name: faction
      },
      organizer: {
        '@type': 'Organization',
        name: faction,
        url: factions[enemy].url
      },
      offers: {
        '@type': 'Offer',
        url: SITE_URL,
        price: 0,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        validFrom: new Date(event.start_time * 1000)
      }
    };
  }
}